import type { SubjectExpertiseView } from "@/services/marketplaceService";

interface SubjectExpertiseProps {
  subjectExpertise: SubjectExpertiseView[];
}

const depthConfig: Record<string, { label: string; steps: number; color: string; badge: string; accent: string }> = {
  FOUNDATIONAL:  { label: "Foundational",     steps: 1, color: "bg-slate-400",   badge: "bg-slate-100 text-slate-600 border-slate-200",          accent: "bg-slate-300" },
  PRIMARY_LEVEL: { label: "Primary",          steps: 1, color: "bg-slate-400",   badge: "bg-slate-100 text-slate-600 border-slate-200",          accent: "bg-slate-300" },
  INTERMEDIATE:  { label: "Intermediate",     steps: 2, color: "bg-sky-500",     badge: "bg-sky-50 text-sky-700 border-sky-200",                 accent: "bg-sky-400" },
  SCHOOL:        { label: "School",           steps: 2, color: "bg-sky-500",     badge: "bg-sky-50 text-sky-700 border-sky-200",                 accent: "bg-sky-400" },
  ADVANCED:      { label: "Advanced",         steps: 3, color: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700 border-emerald-200",     accent: "bg-emerald-400" },
  BOARD_PLUS:    { label: "Board+",           steps: 3, color: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700 border-emerald-200",     accent: "bg-emerald-400" },
  UNIVERSITY:    { label: "University",       steps: 4, color: "bg-violet-500",  badge: "bg-violet-50 text-violet-700 border-violet-200",        accent: "bg-violet-400" },
  COMPETITIVE:   { label: "Competitive Exam", steps: 5, color: "bg-amber-500",   badge: "bg-amber-50 text-amber-700 border-amber-200",           accent: "bg-amber-400" },
};

const TOTAL_STEPS = 5;

function DepthBar({ steps, color }: { steps: number; color: string }) {
  return (
    <div className="flex gap-1 items-center">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
        <div key={i} className={`h-1.5 w-4 rounded-full transition-colors ${i < steps ? color : "bg-slate-200"}`} />
      ))}
    </div>
  );
}

export function SubjectExpertise({ subjectExpertise }: SubjectExpertiseProps) {
  if (!subjectExpertise || subjectExpertise.length === 0) return null;

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="h-5 w-1 rounded-full bg-gradient-to-b from-emerald-500 to-amber-500" />
        <h2 className="text-xl font-bold text-foreground">Subject Expertise</h2>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 dark:bg-slate-800/50 dark:border-slate-700 overflow-hidden divide-y divide-slate-100 dark:divide-slate-700">
        {subjectExpertise.map((entry, i) => {
          const cfg = depthConfig[entry.depthLevel] ?? depthConfig.FOUNDATIONAL;
          return (
            <div key={i} className="flex items-center justify-between gap-4 px-5 py-3.5">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-1 h-8 rounded-full shrink-0 ${cfg.accent}`} />
                <span className="text-sm font-semibold text-foreground truncate">{entry.subjectName}</span>
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${cfg.badge}`}>
                  {cfg.label}
                </span>
                <DepthBar steps={cfg.steps} color={cfg.color} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
